/**
 * Livara collection-card enhancements.
 * ------------------------------------------------------------------
 * Shows the PRE-ORDER badge on product cards for products that Globo
 * Pre-order marks as pre-order. The app-embed block inlines the store's
 * pre-order configuration into window.GloboPreorderParams.preorderSettings
 * ({products: {productId: {variantId: profileId}}}), so no extra API call
 * is needed — we read that global directly.
 *
 * Rules:
 * - PRE-ORDER badge and the "Pre-order" stock label show only when ALL
 *   variants of a product are set as pre-order in the app.
 * - "In stock" when any SKU has stock; "Out of stock" when none do.
 * - Cards rendered with pending stock (0 qty but continue-selling, i.e.
 *   possible pre-order SKUs) resolve once data arrives: all-pre-order ->
 *   "Pre-order", otherwise -> "out of stock".
 */
(function () {
  'use strict';

  function preorderProducts() {
    var p = window.GloboPreorderParams;
    return (p && p.preorderSettings && p.preorderSettings.products) || null;
  }

  // Any single variant is a pre-order variant (card rule: "Pre-order" shows
  // when >=1 sku is pre-order in the Globo map, even if other skus have stock).
  function anyVariantPreorder(pid, variantIds, prods) {
    var map = pid ? prods[pid] : null;
    if (!map) return false;
    var ids = (variantIds || '').split(',').filter(Boolean);
    return ids.some(function (id) {
      return !!map[id];
    });
  }

  // A specific variant is a pre-order variant when it appears in the Globo
  // pre-order map ({productId: {variantId: profileId}}) — the source of
  // truth, set manually by ops in the app (products carry no "preorder" tag).
  function isPreorderVariant(pid, vid, prods) {
    if (!prods || !pid || !vid) return false;
    var m = prods[pid];
    return !!(m && m[vid]);
  }

  // Re-derive the two places that should key off the Globo pre-order data:
  //  1. Product-detail inventory line — when the selected variant is a
  //     pre-order variant, replace "In stock / Out of stock" with a black
  //     "Pre-order" marker.
  //  2. Cart reminder — show the light-blue "Pre-order item" box when any
  //     cart line is a pre-order variant.
  function syncPreorder(root) {
    var prods = preorderProducts();
    var scope = root && root.querySelectorAll ? root : document;

    // (1) Product detail inventory line.
    // The server-rendered block (refreshed by product-info.js on every variant
    // switch) is the base; this overlay swaps it for the black "Pre-order"
    // marker when the *current* variant is a pre-order variant. The guard
    // records the variant id the marker was last applied for, so a variant
    // switch re-applies it (fresh pre-order variant) or reverts it.
    scope.querySelectorAll('.product__inventory[data-livara-variant-id]').forEach(function (el) {
      var vid = el.getAttribute('data-livara-variant-id');
      var host = el.closest('product-info') || el.closest('[data-product-id]');
      var pid = host ? host.getAttribute('data-product-id') : null;
      if (isPreorderVariant(pid, vid, prods)) {
        if (el.getAttribute('data-livara-preorder-applied') !== vid) {
          el.setAttribute('data-livara-preorder-applied', vid);
          el.classList.add('is-preorder');
          el.classList.remove('visibility-hidden');
          el.innerHTML =
            '<svg width="15" height="15" aria-hidden="true">' +
            '<circle cx="7.5" cy="7.5" r="7.5" fill="rgb(17,17,17, 0.15)"/>' +
            '<circle cx="7.5" cy="7.5" r="5" stroke="rgb(255,255,255)" stroke-width="1" fill="rgb(17,17,17)"/>' +
            '</svg><span class="inventory-preorder">Pre-order</span>';
        }
      } else {
        // Revert when the server block no longer belongs to the marked variant
        // (variant switched off a pre-order variant, or initial render).
        if (el.getAttribute('data-livara-preorder-applied') !== null && el.getAttribute('data-livara-preorder-applied') !== vid) {
          el.removeAttribute('data-livara-preorder-applied');
          el.classList.remove('is-preorder');
        }
      }
    });

    // (2) Cart reminder
    var reminders = scope.querySelectorAll('[data-livara-preorder-reminder]');
    if (!reminders.length) return;
    var any = false;
    scope.querySelectorAll('[data-livara-product-id][data-livara-variant-id]').forEach(function (row) {
      if (isPreorderVariant(row.getAttribute('data-livara-product-id'), row.getAttribute('data-livara-variant-id'), prods)) {
        any = true;
      }
    });
    reminders.forEach(function (rem) {
      if (rem.hidden === !any) return; // already in the right state; avoid mutation
      rem.hidden = !any;
    });
  }

  function sync(root) {
    var prods = preorderProducts();
    if (!prods) return false;
    var scope = root && root.querySelectorAll ? root : document;

    // Card decision tree (one coherent pass, keyed on the card):
    //   1. has stock?            -> "In stock"
    //   2. no stock + any pre-order
    //      variant -> "Pre-order" (black) + top-left Pre-order badge
    //   3. no stock, not pre-order -> "Out of stock"
    // Stock + the Pre-order badge live in the same card, so resolve both here.
    scope.querySelectorAll('[data-livara-stock]').forEach(function (el) {
      var card = el.closest('.wbproduct-container') || el.closest('product-info');
      if (!card) return;

      var stockFlag = el.getAttribute('data-livara-in-stock');
      var inStock = stockFlag !== null ? stockFlag === '1' : el.classList.contains('is-in');

      if (inStock) {
        el.classList.remove('is-out', 'is-preorder');
        el.classList.add('is-in');
        el.textContent = 'In stock';
        el.hidden = false;
        var b = card.querySelector('[data-livara-preorder-badge]');
        if (b) b.hidden = true;
        return;
      }

      // No stock: check whether ANY variant is a pre-order variant.
      var pid = card.querySelector('[data-product-id]');
      pid = pid ? pid.getAttribute('data-product-id') : null;
      var badge = card.querySelector('[data-livara-preorder-badge]');
      var bvids = badge ? badge.getAttribute('data-livara-variants') : el.getAttribute('data-livara-variants');

      if (anyVariantPreorder(pid, bvids, prods)) {
        el.classList.remove('is-in', 'is-out');
        el.classList.add('is-preorder');
        el.textContent = 'Pre-order';
        el.hidden = false;
        if (badge) badge.hidden = false;
      } else {
        el.classList.remove('is-in', 'is-preorder');
        el.classList.add('is-out');
        el.textContent = 'Out of stock';
        el.hidden = false;
        if (badge) badge.hidden = true;
      }
    });

    return true;
  }

  // -------------------------------------------------------------
  // Colour preview dots: show a SOLID colour derived from the colour
  // NAME, not the uploaded image. Image-based option swatches (the common
  // case) are converted to their named colour once this script runs.
  // -------------------------------------------------------------

  var COLOR_BY_NAME = {
    white: '#ffffff', ivory: '#f7f3ea', cream: '#f2ead8', beige: '#d7c4a1',
    taupe: '#b9a18a', tan: '#c8a882', sand: '#dcc9a8', greige: '#bfb3a1',
    grey: '#8a8a8a', gray: '#8a8a8a', charcoal: '#3a3a3a', black: '#111111',
    brown: '#6f4e37', walnut: '#5d4326', oak: '#b98c5f', caramel: '#a9793e',
    camel: '#b98a54', cognac: '#9a5b2f', chestnut: '#7a4a2b', coffee: '#6f4e37',
    rust: '#9c4a2f', green: '#4c7a34', olive: '#6b7a35', sage: '#8aa08a',
    forest: '#2f5d3a', khaki: '#9a9470', blue: '#3b6ea5', navy: '#1f3a5f',
    denim: '#4a6592', sky: '#8fb7d9', teal: '#2f7a7a', turquoise: '#4a9a9a',
    red: '#b23a2e', burgundy: '#6f1f1f', terracotta: '#b8603a', brick: '#a8442f',
    orange: '#cc7a2a', rust: '#9c4a2f', yellow: '#e0b33c', gold: '#c7a33c',
    pink: '#d99aa0', blush: '#e5c0c4', rose: '#c97a83', purple: '#6d5a9e',
    mauve: '#9a7a92', lavender: '#b8a4cc', multicolor: '#999999'
  };

  function colorFromName(name) {
    var key = String(name || '').toLowerCase().trim();
    if (!key) return null;
    if (COLOR_BY_NAME[key]) return COLOR_BY_NAME[key];
    // "Taupe / Walnut" -> "Taupe", "Light Grey" -> "Light Grey" then "Light"
    var base = key.split('/')[0].trim();
    if (COLOR_BY_NAME[base]) return COLOR_BY_NAME[base];
    var first = key.split(/\s+/)[0];
    if (COLOR_BY_NAME[first]) return COLOR_BY_NAME[first];
    return null;
  }

  function applySolidColour(dot, colour) {
    dot.style.backgroundImage = 'none';
    dot.style.backgroundColor = colour;
  }

  function bgUrlOf(style) {
    var m = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(style || '');
    return m ? m[1] : null;
  }

  // Average colour of an image (skipping near-white/near-black pixels so a
  // product shot on a light background still yields the fabric colour).
  function sampleImageColour(url, cb) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        var s = 28;
        var c = document.createElement('canvas');
        c.width = s;
        c.height = s;
        var ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, s, s);
        var d = ctx.getImageData(0, 0, s, s).data;
        var r = 0, g = 0, b = 0, n = 0;
        var ra = 0, ga = 0, ba = 0, na = 0;
        for (var i = 0; i < d.length; i += 4) {
          ra += d[i]; ga += d[i + 1]; ba += d[i + 2]; na++;
          var lum = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
          if (lum < 12 || lum > 244) continue; // drop near-black / near-white
          r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
        }
        if (n) cb([Math.round(r / n), Math.round(g / n), Math.round(b / n)]);
        else cb([Math.round(ra / na), Math.round(ga / na), Math.round(ba / na)]);
      } catch (e) {
        cb(null);
      }
    };
    img.onerror = function () { cb(null); };
    img.src = url;
  }

  function colorDotsByTitle(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.livara-color-swatches__dot').forEach(function (dot) {
      if (dot.getAttribute('data-coloured')) return; // already '1' or pending
      var style = dot.getAttribute('style') || '';
      if (style.indexOf('background-image') === -1) {
        dot.setAttribute('data-coloured', '1'); // already a solid colour
        return;
      }
      dot.setAttribute('data-coloured', 'pending');

      // 1) colour from the name
      var hex = colorFromName(dot.getAttribute('title'));
      if (hex) {
        applySolidColour(dot, hex);
        dot.setAttribute('data-coloured', '1');
        return;
      }

      // 2) unknown name -> sample the swatch image for a representative colour
      var url = bgUrlOf(style);
      if (!url) {
        dot.setAttribute('data-coloured', '1');
        return;
      }
      var clean = url.indexOf('//') === 0 ? 'https:' + url : url;
      sampleImageColour(clean, function (rgb) {
        if (rgb) applySolidColour(dot, 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')');
        dot.setAttribute('data-coloured', '1');
      });
    });
  }

  function trySync() {
    sync(document);
    syncPreorder(document);
    colorDotsByTitle(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trySync);
  } else {
    trySync();
  }

  // Globo inlines its params before deferred scripts run, but re-check
  // around load time in case the app embed renders or re-asserts late.
  window.addEventListener('load', function () {
    trySync();
    setTimeout(trySync, 1500);
  });
  document.addEventListener('shopify:section:load', trySync);
  window.addEventListener('collectionUpdate', trySync);

  // Ajax pagination / filtering re-renders cards; catch the new badges.
  var pendingSync = null;
  var observer = new MutationObserver(function () {
    clearTimeout(pendingSync);
    pendingSync = setTimeout(trySync, 120);
  });
  var boot = function () {
    observer.observe(document.body, { childList: true, subtree: true });
  };
  if (document.body) {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }

  window.LivaraBadges = { sync: sync };
})();
